import type { WorkSourceDto } from './work';
import { z } from 'zod';
import { ExecutorConnectionOperationSchema } from './executors';

/**
 * Connettore generico REST: come leggere le API di un software, senza codice per prodotto.
 * La definizione la propone il motore dalla conoscenza pubblica del software; si prova con le
 * credenziali dell'azienda, si guarda l'anteprima e si conferma.
 */
const ident = z.string().regex(/^[a-z][a-z0-9_]{0,62}$/);
const dotPath = z.string().max(200).regex(/^[A-Za-z0-9_.\-[\]]*$/);

export const RestAuthSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('none') }),
  z.object({ type: z.literal('api_key_header'), header: z.string().regex(/^[A-Za-z0-9-]{1,60}$/), credential: ident }),
  z.object({ type: z.literal('bearer'), credential: ident }),
  z.object({ type: z.literal('basic'), username: ident, password: ident }),
  /**
   * Accesso con login: si chiama un indirizzo di autenticazione con le credenziali dell'utente (es.
   * username e password, oppure client id e secret) e si usa il token restituito nelle chiamate.
   * È il caso di molte API italiane (fatturazione elettronica, gestionali) e di OAuth2 password/client credentials.
   */
  z.object({
    type: z.literal('login'),
    /** Indirizzo completo (https) o percorso relativo a base_url; ammette {{chiave}}. */
    url: z.string().min(1).max(300),
    format: z.enum(['form', 'json']).default('form'),
    /** Campi inviati al login: valori fissi o {{chiave}} delle credenziali. */
    body: z.record(z.string().max(300)).refine((b) => Object.keys(b).length > 0 && Object.keys(b).length <= 10, 'da 1 a 10 campi'),
    token_path: dotPath.min(1).default('access_token'),
    header: z.string().regex(/^[A-Za-z0-9-]{1,60}$/).default('Authorization'),
    prefix: z.string().max(20).default('Bearer '),
  }),
  /**
   * OAuth2 con consenso nel browser (authorization code + PKCE): l'utente accede al servizio e autorizza;
   * il sistema conserva il permesso (refresh token) cifrato e lo rinnova a ogni giro. Client id e secret
   * sono quelli dell'applicazione che l'azienda registra presso il servizio.
   */
  z.object({
    type: z.literal('oauth2_code'),
    /** https: verificato all'uso (come gli altri indirizzi del connettore). */
    authorize_url: z.string().url().max(400),
    token_url: z.string().url().max(400),
    scopes: z.array(z.string().max(200)).max(20).default([]),
    scope_separator: z.string().max(3).default(' '),
    client_id: ident,
    client_secret: ident.nullable().default(null),
    /** Parametri aggiuntivi della pagina di consenso (es. access_type=offline per avere il rinnovo). */
    authorize_params: z.record(z.string().max(200)).default({}),
    /** Come si presentano client id e secret allo scambio del codice: nel corpo o in Basic. */
    token_auth: z.enum(['body', 'basic']).default('body'),
  }),
  /**
   * Sessione del sito (ADR 0011): il software non ha API pubbliche, ma le sue pagine chiamano il proprio server.
   * Il sistema entra dalla pagina di accesso con un browser e le credenziali dell'utente, poi chiama gli stessi
   * indirizzi interni con la sessione ottenuta (cookie e intestazioni di autorizzazione viste nel sito).
   */
  z.object({
    type: z.literal('web_session'),
    /** Pagina di accesso (https); ammette {{chiave}}. */
    login_url: z.string().min(8).max(400),
    username: ident,
    password: ident,
    /** Pagina da aprire dopo l'accesso, dove il sito fa le chiamate (così si vedono le intestazioni che usa, es. CSRF). */
    warmup_url: z.string().max(500).nullable().default(null),
  }),
]);

export const RestPaginationSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('none') }),
  z.object({ type: z.literal('page'), param: z.string().max(40), start: z.number().int().min(0).default(1), size_param: z.string().max(40).optional(), size: z.number().int().min(1).max(500).optional() }),
  z.object({ type: z.literal('offset'), param: z.string().max(40), size_param: z.string().max(40), size: z.number().int().min(1).max(500) }),
  z.object({ type: z.literal('cursor'), response_field: dotPath, request_field: z.string().max(60), in: z.enum(['query', 'body']) }),
]);

/** Campo letto da una risorsa: nome interno, percorso nella risposta, tipo ed etichetta leggibile (ADR 0012). */
export const RestFieldSchema = z.object({ name: ident, path: dotPath, tipo: z.enum(['testo', 'numero', 'data']), label: z.string().min(1).max(80).optional() });
export type RestField = z.infer<typeof RestFieldSchema>;

export const RestResourceSchema = z.object({
  key: ident,
  label: z.string().min(1).max(80),
  method: z.enum(['GET', 'POST']).default('GET'),
  path: z.string().min(1).max(500),
  query: z.record(z.string()).default({}),
  body: z.record(z.unknown()).optional(),
  items_path: dotPath.default(''),
  id_field: dotPath,
  pagination: RestPaginationSchema.default({ type: 'none' }),
  /**
   * Finestra di date (ADR 0145): il servizio vuole sempre un intervallo di date, al massimo di `days` giorni. Si legge
   * una finestra alla volta, dalla più recente; dentro la finestra vale la paginazione.
   */
  date_window: z
    .object({
      start_param: z.string().min(1).max(40),
      end_param: z.string().min(1).max(40),
      days: z.number().int().min(1).max(366),
      format: z.enum(['date', 'datetime']).default('date'),
      per_minute: z.number().int().min(1).max(600).optional(),
    })
    .optional(),
  fields: z.array(RestFieldSchema).min(1).max(60),
  /**
   * Per le risorse che sono file (archivi documenti in cloud, gestione documentale): come scaricarne il
   * contenuto. Nel percorso {{rif}} è l'identificativo dell'elemento e {{campo}} i suoi campi. `content_path`: il
   * file sta in Base64 a quel percorso di una risposta JSON (es. il dettaglio di una fattura elettronica), altrimenti
   * la risposta è il file. `version_fields`: i campi che cambiano il file; senza, una riga cambiata è un file nuovo
   * (ADR 0169).
   */
  download: z
    .object({
      path: z.string().min(1).max(500),
      name_field: ident.optional(),
      mime_field: ident.optional(),
      content_path: z.string().min(1).max(200).optional(),
      version_fields: z.array(ident).min(1).max(10).optional(),
    })
    .optional(),
});

/** Letture per collegamento: oltre, la sincronizzazione diventa troppo lunga per un giro orario. */
export const MAX_RESOURCES = 10;

export const RestDefinitionSchema = z.object({
  base_url: z.string().min(8).max(300),
  auth: RestAuthSchema,
  /** Dati da chiedere all'utente per questo servizio: cambiano da servizio a servizio (anche a scelta, es. ambiente). */
  credentials: z
    .array(
      z.object({
        key: ident,
        label: z.string().max(120),
        secret: z.boolean(),
        help: z.string().max(400).optional(),
        /** Valori ammessi (es. ambiente di produzione o di prova): il modulo mostra una scelta. */
        options: z.array(z.object({ value: z.string().max(300), label: z.string().max(120) })).max(10).optional(),
        default: z.string().max(300).optional(),
      }),
    )
    .max(8),
  /** Chiamata di prova; se manca, la prova è il login (o la prima pagina della prima risorsa). */
  test: z.object({ method: z.enum(['GET', 'POST']).default('GET'), path: z.string().min(1).max(500) }).nullable().default(null),
  resources: z.array(RestResourceSchema).max(MAX_RESOURCES),
  executor_operations: z.array(ExecutorConnectionOperationSchema).max(40).optional(),
}).superRefine((d, ctx) => {
  if (!d.resources.length && !d.executor_operations?.length) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Serve almeno una lettura o un’operazione esplicita del sistema', path: ['resources'] });
  // Ogni {{chiave}} usata deve essere chiesta all'utente, e ogni dato chiesto deve servire a qualcosa.
  const keys = new Set(d.credentials.map((c) => c.key));
  const used = new Set<string>();
  const scan = (v: unknown) => JSON.stringify(v ?? '').replace(/\{\{([a-z][a-z0-9_]*)\}\}/g, (_m, k: string) => (k !== 'oggi' && used.add(k), ''));
  scan(d.base_url);
  scan(d.auth);
  scan(d.test);
  // Nel percorso di download {{rif}} e i campi della risorsa non sono credenziali.
  scan(d.resources.map(({ download, ...r }) => ({ ...r, download: download ? { ...download, path: download.path.replace(/\{\{([a-z][a-z0-9_]*)\}\}/g, (m, k: string) => (k === 'rif' || r.fields.some((f) => f.name === k) ? '' : m)) } : undefined })));
  if (d.auth.type === 'api_key_header' || d.auth.type === 'bearer') used.add(d.auth.credential);
  if (d.auth.type === 'basic') (used.add(d.auth.username), used.add(d.auth.password));
  if (d.auth.type === 'oauth2_code') (used.add(d.auth.client_id), d.auth.client_secret && used.add(d.auth.client_secret));
  if (d.auth.type === 'web_session') (used.add(d.auth.username), used.add(d.auth.password));
  for (const [i, r] of d.resources.entries())
    for (const f of r.download?.version_fields ?? [])
      if (!r.fields.some((x) => x.name === f)) ctx.addIssue({ code: z.ZodIssueCode.custom, message: `version_fields: "${f}" non è tra i campi`, path: ['resources', i, 'download', 'version_fields'] });
  if (d.auth.type !== 'none' && !d.credentials.length) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'con un accesso autenticato servono i dati da chiedere all’utente', path: ['credentials'] });
  if (d.auth.type === 'login' && !/\{\{[a-z][a-z0-9_]*\}\}/.test(JSON.stringify(d.auth.body)))
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'il login deve inviare le credenziali dell’utente; una pagina di consenso nel browser è oauth2_code, non login', path: ['auth', 'body'] });
  for (const k of used) if (!keys.has(k)) ctx.addIssue({ code: z.ZodIssueCode.custom, message: `credenziale "${k}" usata ma non chiesta all'utente`, path: ['credentials'] });
  for (const k of keys) if (!used.has(k)) ctx.addIssue({ code: z.ZodIssueCode.custom, message: `credenziale "${k}" chiesta ma mai usata`, path: ['credentials'] });
});
export type RestDefinition = z.infer<typeof RestDefinitionSchema>;
export type RestResource = z.infer<typeof RestResourceSchema>;

/**
 * Ricetta di navigazione (ADR 0011): per i software senza API né chiamate interne utilizzabili, passi
 * dichiarativi ricavati dal modello una sola volta esplorando il sito, poi eseguiti da un browser senza modello.
 */
const selector = z.string().min(1).max(300);
/** Indirizzo di una pagina: completo (https) o percorso relativo all'indirizzo del sito; ammette {{chiave}}. */
const pageUrl = z.string().min(1).max(500);

export const PageStepSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('goto'), url: pageUrl }),
  z.object({ action: z.literal('click'), selector }),
  /** Valore fisso, {{chiave}} delle credenziali o, nelle scritture, {{campo}} dei dati da scrivere. */
  z.object({ action: z.literal('fill'), selector, value: z.string().max(500) }),
  z.object({ action: z.literal('select'), selector, value: z.string().max(200) }),
  z.object({ action: z.literal('wait'), selector }),
]);
export type PageStep = z.infer<typeof PageStepSchema>;

export const PageResourceSchema = z.object({
  key: ident,
  label: z.string().min(1).max(80),
  /** Come si arriva all'elenco (di solito una sola apertura di pagina). */
  steps: z.array(PageStepSchema).min(1).max(10),
  /** Un elemento per riga dell'elenco (es. `table#fatture tbody tr`). */
  rows: selector,
  /** Campo che identifica la riga (deve essere tra i campi). */
  id_field: ident,
  /** Selettore relativo alla riga (vuoto = la riga stessa); `attr` legge un attributo invece del testo. */
  fields: z.array(z.object({ name: ident, selector: z.string().max(300), attr: z.string().max(60).nullable().default(null), tipo: z.enum(['testo', 'numero', 'data']) })).min(1).max(60),
  /** Pulsante o link della pagina successiva; null se l'elenco è tutto in una pagina. */
  next: selector.nullable().default(null),
  /** Righe che sono documenti: `field` è il campo con il link al file (letto con attr href), scaricato con la sessione. */
  download: z.object({ field: ident, name_field: ident.optional(), mime_field: ident.optional() }).nullable().default(null),
  /** Righe con allegati nella scheda di dettaglio: `field` è il campo con il link alla scheda; i file si scaricano da lì. */
  attachments: z.object({ field: ident }).nullable().default(null),
});
export type PageResource = z.infer<typeof PageResourceSchema>;

/** Scrittura nel software: si esegue solo dopo l'anteprima e la conferma dell'operatore. */
export const PageWriteSchema = z.object({
  key: ident,
  label: z.string().min(1).max(80),
  /** Dati da chiedere all'operatore; nei passi si usano come {{nome}}. */
  inputs: z.array(z.object({ name: ident, label: z.string().min(1).max(120), tipo: z.enum(['testo', 'numero', 'data']), required: z.boolean().default(true) })).min(1).max(20),
  /** Apertura del modulo e compilazione, senza invio. */
  steps: z.array(PageStepSchema).min(1).max(20),
  /** Pulsante che invia il modulo: si preme solo dopo la conferma. */
  submit: selector,
  /** Dopo l'invio si rilegge questa lettura e si cerca una riga con questi campi uguali ai dati scritti. */
  verify: z.object({ resource: ident, match: z.array(z.object({ field: ident, input: ident })).min(1).max(5) }).nullable().default(null),
});
export type PageWrite = z.infer<typeof PageWriteSchema>;

/** Letture per collegamento dal sito: un gestionale ha spesso più tipi di dato di quanti ne esponga un'API. */
export const MAX_PAGE_RESOURCES = 30;

export const PageDefinitionSchema = z
  .object({
    kind: z.literal('pagine'),
    /** Pagina di accesso (https); ammette {{chiave}}. */
    login_url: z.string().min(8).max(400),
    username: ident,
    password: ident,
    credentials: RestDefinitionSchema._def.schema.shape.credentials,
    resources: z.array(PageResourceSchema).min(1).max(MAX_PAGE_RESOURCES),
    writes: z.array(PageWriteSchema).max(10).default([]),
  })
  .superRefine((d, ctx) => {
    const keys = new Set(d.resources.map((r) => r.key));
    for (const [i, r] of d.resources.entries())
      if (!r.fields.some((f) => f.name === r.id_field)) ctx.addIssue({ code: z.ZodIssueCode.custom, message: `id_field "${r.id_field}" non è tra i campi`, path: ['resources', i, 'id_field'] });
    for (const [i, r] of d.resources.entries())
      if (r.download && !r.fields.some((f) => f.name === r.download!.field)) ctx.addIssue({ code: z.ZodIssueCode.custom, message: `campo del file "${r.download.field}" non è tra i campi`, path: ['resources', i, 'download'] });
    for (const [i, r] of d.resources.entries())
      if (r.attachments && !r.fields.some((f) => f.name === r.attachments!.field)) ctx.addIssue({ code: z.ZodIssueCode.custom, message: `campo della scheda "${r.attachments.field}" non è tra i campi`, path: ['resources', i, 'attachments'] });
    for (const [i, w] of d.writes.entries()) {
      if (w.verify && !keys.has(w.verify.resource)) ctx.addIssue({ code: z.ZodIssueCode.custom, message: `verifica su una lettura inesistente "${w.verify.resource}"`, path: ['writes', i, 'verify'] });
      const names = new Set(w.inputs.map((x) => x.name));
      for (const m of w.verify?.match ?? []) if (!names.has(m.input)) ctx.addIssue({ code: z.ZodIssueCode.custom, message: `dato "${m.input}" non chiesto`, path: ['writes', i, 'verify'] });
    }
  });
export type PageDefinition = z.infer<typeof PageDefinitionSchema>;

/** Definizione salvata in un collegamento: API (anche con la sessione del sito) o ricetta di navigazione. */
export type ConnectionDefinition = RestDefinition | PageDefinition;
export const isPageDefinition = (d: unknown): d is PageDefinition => !!d && typeof d === 'object' && (d as { kind?: unknown }).kind === 'pagine';

export interface IntegrationAssessment {
  key: string;
  name: string;
  vendor: string | null;
  category: string | null;
  has_api: boolean;
  api_docs_url: string | null;
  auth: string | null;
  has_official_mcp: boolean;
  mcp_url: string | null;
  readable: string[];
  writable: string[];
  /** dal_sito: senza API utilizzabili, ci si collega entrando dal sito con le credenziali (ADR 0011). */
  feasibility: 'collegabile' | 'dal_sito' | 'solo_export' | 'manuale';
  confidence: 'bassa' | 'media' | 'alta';
  notes: string | null;
  verified: boolean;
  has_template: boolean;
  /** Valutata con regole precedenti: va rifatta. */
  outdated: boolean;
  /** Server a cui verranno inviate le credenziali. */
  connection_hosts: string[];
  /** Accesso con consenso nel browser (OAuth2): l'utente autorizza dalla pagina del servizio. */
  oauth: boolean;
  /** Dati che l'azienda dovrà inserire per collegarsi (dalla bozza di collegamento). */
  connection_fields: Array<{ key: string; label: string; secret: boolean; help?: string; options?: Array<{ value: string; label: string }>; default?: string }>;
  /** Da dove viene la valutazione: specifica ufficiale delle API, documentazione consultata, o solo memoria del modello. */
  doc_source: 'specifica_api' | 'documentazione' | 'memoria';
  /** Pagine della documentazione consultate e specifica letta. */
  sources: string[];
  spec_url: string | null;
  /** Collegamento dal sito: possibile, o perché no (condizioni d'uso, verifica in due passaggi, captcha, niente area web). */
  web: { status: 'possibile' | 'vietato' | 'due_passaggi' | 'captcha' | 'no'; login_url: string | null };
  assessed_at: string;
}

/** Un'utenza collegata di un software (ADR 0228). */
export interface SoftwareConnectionDto {
  id: string;
  /** Riferimento dell'utenza nella coda, nell'archivio e nei file scaricati (la prima ha la chiave del software). */
  ref: string;
  /** Nome dato dalla persona per riconoscerla; non dice a che cosa si riferiscono i dati. */
  label: string | null;
  status: string;
  syncing: boolean;
  /** Sincronizzazione nella coda di lavorazione (ADR 0043): righe lette, tempo stimato, errori. */
  progress: WorkSourceDto | null;
  last_sync_at: string | null;
  last_result: unknown;
  last_error: string | null;
  /** api, sessione del sito (chiamate interne) o pagine (ricetta di navigazione). */
  kind: 'api' | 'sessione_sito' | 'pagine';
  /** Il sito è cambiato: va rifatta la mappatura. */
  remap: boolean;
  /** Ogni quante ore si rilegge (collegamenti dal sito). */
  sync_hours: number;
  /** Scritture disponibili (ricetta di navigazione): si eseguono solo dopo anteprima e conferma. */
  writes: Array<{ key: string; label: string; inputs: Array<{ name: string; label: string; tipo: 'testo' | 'numero' | 'data'; required: boolean }> }>;
  /** Scelta dei campi per l'azienda (ADR 0012): in corso, ultimo esito. */
  choosing_fields: boolean;
  fields_choice: FieldChoiceOutcome | null;
}

export interface SoftwareDto {
  id: string;
  key: string;
  name: string;
  category: string | null;
  /** Prodotto indicato dall'utente quando il nome usato in azienda è generico. */
  product: string | null;
  /** Dal nome non si riconosce un prodotto preciso: serve che l'utente dica qual è. */
  product_unrecognized: boolean;
  usages: Array<{ id: string; process_key: string | null; info_key: string | null; how: string; description: string | null; source: string; created_at: string }>;
  /** Firme dei file (layout di export) che arrivano da questo software. */
  signature_ids: string[];
  /** Quante volte negli ultimi 30 giorni sono arrivati file da questo software (dalle firme collegate). */
  uploads_30d: number;
  assessment: IntegrationAssessment | null;
  /** Verifica in corso (lettura della documentazione). */
  assessing: boolean;
  /** Per l'accesso OAuth2: indirizzo di ritorno da registrare nell'applicazione presso il servizio. */
  oauth_redirect_uri: string | null;
  /** La prima utenza collegata (ADR 0228), o nessuna. */
  connection: SoftwareConnectionDto | null;
  /** Tutte le utenze collegate del software, ognuna con il suo stato (ADR 0228). */
  connections: SoftwareConnectionDto[];
  /** Esplorazione del sito in corso o appena finita (collegamento dal sito). */
  exploration: WebExplorationDto | null;
}

export interface WebExplorationDto {
  status: 'in_corso' | 'pronta' | 'errore' | 'non_possibile';
  /** Cosa sta facendo (es. «lettura della pagina /fatture»). */
  step: string | null;
  error: string | null;
  /** Strada trovata: chiamate interne del sito o ricetta di navigazione. */
  kind: 'sessione_sito' | 'pagine' | null;
  preview: ConnectionPreview | null;
  /** Quanto ha letto: pagine aperte e chiamate interne viste. */
  explored: { pages: number; calls: number } | null;
  started_at: string;
}

/**
 * Quale utenza si collega (ADR 0228): `connection_id` ricollega un'utenza esistente, `label` ne aggiunge una nuova con
 * quel nome. Senza nessuno dei due, con una sola utenza già presente, si ricollega quella.
 */
const accountFields = {
  connection_id: z.string().uuid().optional(),
  label: z.string().trim().min(1).max(120).optional(),
};

/** Collegamento dal sito: pagina di accesso, utente e password, consenso esplicito dell'utente. */
export const WebConnectionInputSchema = z.object({
  login_url: z.string().trim().url().max(400),
  username: z.string().trim().min(1).max(300),
  password: z.string().min(1).max(500),
  /** Conferma che le condizioni d'uso consentono l'accesso automatico e autorizza il sistema a entrare. */
  consent: z.literal(true),
  sync_hours: z.coerce.number().int().refine((h) => [1, 6, 24].includes(h), 'ogni 1, 6 o 24 ore').default(6),
  ...accountFields,
});
export type WebConnectionInput = z.infer<typeof WebConnectionInputSchema>;

/** Scrittura nel software collegato dal sito: prima l'anteprima, poi la conferma con il codice dell'anteprima. */
export const WebWritePreviewSchema = z.object({ write: ident, values: z.record(z.string().max(2000)) });
export const WebWriteConfirmSchema = z.object({ token: z.string().min(20).max(100) });

export const AddSoftwareSchema = z.object({
  name: z.string().trim().min(1).max(120),
  category: z.string().trim().max(60).optional(),
  how: z.string().trim().max(80).default('dichiarato'),
  description: z.string().trim().max(500).optional(),
  /** Collega una firma di file (export arrivati da questo software). */
  signature_id: z.string().uuid().optional(),
});

export const AssessSoftwareSchema = z.object({
  /** Indicazione su come si accede alle API (mai credenziali). */
  hint: z.string().trim().max(500).optional(),
  /** Nome del prodotto, quando quello usato in azienda è generico (es. «server interno» → «Google Drive»). */
  product: z.string().trim().min(1).max(120).optional(),
});

export const ConnectionInputSchema = z.object({
  credentials: z.record(z.string().max(2000)),
  /** Definizione modificata a mano (avanzato); di default quella proposta dal catalogo. */
  definition: RestDefinitionSchema.optional(),
  /** Collega solo i dati che la prova ha letto senza errori (gli altri si tolgono dalla definizione). */
  only_working: z.boolean().optional(),
  ...accountFields,
});
export type ConnectionInput = z.infer<typeof ConnectionInputSchema>;

export interface ConnectionPreview {
  ok: boolean;
  test: string;
  resources: Array<{ key: string; label: string; rows: number; sample: Array<Record<string, unknown>>; error: string | null }>;
  /** La prima prova non tornava: il sistema ha corretto il collegamento dalla documentazione e questa è la seconda. */
  repaired?: string;
}

/** Esito della scelta dei campi di un collegamento API per l'azienda (ADR 0012). */
export interface FieldChoiceOutcome {
  at: string;
  ok: boolean;
  /** Una frase per l'utente: cosa si legge ora, o perché la scelta non è riuscita. */
  note: string;
}

/** Campo trovato nel campione reale di una lettura: percorso nella risposta, valore di esempio, in quanti elementi c'è. */
export interface AvailableField {
  path: string;
  example: string;
  present: number;
  sample: number;
}

/** Campi di un collegamento API: quelli letti e quelli disponibili nel campione, per lettura. */
export interface ConnectionFieldsDto {
  choice: FieldChoiceOutcome | null;
  choosing: boolean;
  resources: Array<{ key: string; label: string; fields: RestField[]; available: AvailableField[] }>;
}

export const SetConnectionFieldsSchema = z.object({
  resource: ident,
  fields: z.array(RestFieldSchema).min(1).max(60),
});
export type SetConnectionFieldsInput = z.infer<typeof SetConnectionFieldsSchema>;
